/**
 * -----------------------------------------------
 * QUICK ADD FUNCTIONALITY - IMPLEMENTATION GUIDE
 * -----------------------------------------------
 * 
 * This file provides the quick-add modal functionality for product cards and sections.
 * Below is a comprehensive guide on how to implement quick add in your Shopify theme sections.
 * 
 * REQUIRED FILES:
 * - quick-add.js       : Handles modal interactions for multi-variant products
 * - product-form.js    : Handles form submissions and AJAX cart updates
 * 
 * IMPLEMENTATION STEPS:
 * 
 * 1. INCLUDE SCRIPTS IN YOUR SECTION:
 *    <script src="{{ 'quick-add.js' | asset_url }}" defer="defer"></script>
 *    <script src="{{ 'product-form.js' | asset_url }}" defer="defer"></script>
 * 
 * 2. BASIC IMPLEMENTATION (SINGLE VARIANT/FIRST AVAILABLE):
 *    Use this approach for directly adding the first available variant to cart:
 * 
 *    {%- liquid
 *      assign product_form_id = 'quick-add-' | append: section.id | append: product.id
 *      assign available_variant = product.variants | where: 'available', true | first
 *    -%}
 *    
 *    <div class="quick-add no-js-hidden">
 *      <product-form>
 *        {%- form 'product',
 *          product,
 *          id: product_form_id,
 *          class: 'form',
 *          novalidate: 'novalidate',
 *          data-type: 'add-to-cart-form'
 *        -%}
 *          <input
 *            type="hidden"
 *            name="id"
 *            value="{{ available_variant.id }}"
 *          >
 *          {%- if available_variant != null -%}
 *          <button
 *            id="{{ product_form_id }}-submit"
 *            type="submit"
 *            name="add"
 *            class="add-to-cart-button-{{ section.id }}"
 *            aria-labelledby="{{ product_form_id }}-submit title-{{ section.id }}-{{ product.id }}"
 *            aria-live="polite"
 *          >
 *            <span class="button-text">
 *              {{ 'products.product.add_to_cart' | t }}
 *            </span>
 *            <div class="loading-overlay__spinner hidden">
 *              <svg
 *                aria-hidden="true"
 *                focusable="false"
 *                class="spinner"
 *                viewBox="0 0 66 66"
 *                xmlns="http://www.w3.org/2000/svg"
 *              >
 *                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
 *              </svg>
 *            </div>
 *          </button>
 *          {%- else -%}
 *          <button
 *            type="button"
 *            class="add-to-cart-button-{{ section.id }} sold-out"
 *            aria-disabled="true"
 *            disabled
 *          >
 *            <span class="button-text sold-out-underlay">
 *              {{ 'products.product.add_to_cart' | t }}
 *            </span>
 *            <span class="sold-out-overlay">{{ 'products.product.sold_out' | t | upcase }}</span>
 *          </button>
 *          {%- endif -%}
 *        {%- endform -%}
 *      </product-form>
 *    </div>
 * 
 * 3. ADVANCED IMPLEMENTATION (MULTI-VARIANT WITH MODAL):
 *    Use this approach when products have multiple variants and you need a modal picker:
 * 
 *    {%- liquid
 *      assign product_form_id = 'quick-add-' | append: section.id | append: product.id
 *      assign qty_rules = false
 *      if product.selected_or_first_available_variant.quantity_rule.min > 1 or product.selected_or_first_available_variant.quantity_rule.max != null or product.selected_or_first_available_variant.quantity_rule.increment > 1
 *        assign qty_rules = true
 *      endif
 *    -%}
 *    
 *    <div class="quick-add no-js-hidden">
 *      {%- if product.variants.size > 1 or qty_rules -%}
 *        <!-- Quick Add Modal Opener -->
 *        <modal-opener data-modal="#QuickAdd-{{ product.id }}">
 *          <button
 *            id="{{ product_form_id }}-submit"
 *            type="button"
 *            class="add-to-cart-button-{{ section.id }} quick-add__submit"
 *            aria-haspopup="dialog"
 *            aria-labelledby="{{ product_form_id }}-submit title-{{ section.id }}-{{ product.id }}"
 *            data-product-url="{{ product.url }}"
 *          >
 *            <!-- Button content (icon or text) -->
 *            <div class="loading-overlay__spinner hidden">
 *              <svg><!-- Spinner SVG --></svg>
 *            </div>
 *          </button>
 *        </modal-opener>
 *        
 *        <!-- Quick Add Modal -->
 *        <quick-add-modal id="QuickAdd-{{ product.id }}" class="quick-add-modal">
 *          <div
 *            role="dialog"
 *            aria-label="{{ 'products.product.choose_product_options' | t: product_name: product.title | escape }}"
 *            aria-modal="true"
 *            class="quick-add-modal__content global-settings-popup"
 *            tabindex="-1"
 *          >
 *            <button
 *              id="ModalClose-{{ product.id }}"
 *              type="button"
 *              class="quick-add-modal__toggle"
 *              aria-label="{{ 'accessibility.close' | t }}"
 *            >
 *              {% render 'icon-close' %}
 *            </button>
 *            <div id="QuickAddInfo-{{ product.id }}" class="quick-add-modal__content-info">
 *            </div>
 *          </div>
 *        </quick-add-modal>
 *      {%- else -%}
 *        <!-- Single variant implementation (same as basic) -->
 *      {%- endif -%}
 *    </div>
 * 
 * 4. REQUIRED CSS STYLES:
 *    Add these essential styles to your section's styles:
 * 
 *    .add-to-cart-button-{{ section.id }} {
 *      position: relative;
 *      display: inline-flex;
 *      align-items: center;
 *      justify-content: center;
 *      padding: 10px 20px;
 *      font-size: 1.6rem;
 *      border: none;
 *      border-radius: 4px;
 *      cursor: pointer;
 *      overflow: hidden;
 *    }
 *    
 *    .add-to-cart-button-{{ section.id }}.sold-out {
 *      background-color: #ccc;
 *      color: #666;
 *      cursor: not-allowed;
 *    }
 *    
 *    .add-to-cart-button-{{ section.id }} .button-text {
 *      visibility: visible;
 *      opacity: 1;
 *      transition: opacity 0.2s, visibility 0.2s;
 *    }
 *    
 *    .add-to-cart-button-{{ section.id }}.loading .button-text {
 *      visibility: hidden;
 *      opacity: 0;
 *    }
 *    
 *    .loading-overlay__spinner {
 *      position: absolute;
 *      top: 50%;
 *      left: 50%;
 *      transform: translate(-50%, -50%);
 *      width: 20px;
 *      height: 20px;
 *      z-index: 2;
 *      display: none;
 *    }
 *    
 *    .add-to-cart-button-{{ section.id }}.loading .loading-overlay__spinner {
 *      display: flex;
 *    }
 *    
 *    .hidden {
 *      display: none !important;
 *    }
 * 
 * 5. HOW IT WORKS:
 *    - For single variant products: Direct add to cart
 *    - For multi-variant products: Opens modal for variant selection
 *    - product-form.js handles the AJAX form submission
 *    - quick-add.js manages the modal and loading states
 * 
 * TROUBLESHOOTING:
 * - If buttons don't work, ensure both JS files are included
 * - Check for proper form structure and attributes
 * - Ensure variant IDs are correct
 * - Check for CSS conflicts
 * 
 */

if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      fetch(opener.getAttribute('data-product-url'))
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          super.show(opener);
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}
